import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'

export class LEDBanner extends UcoastEl {
	static htmlSelector = 'led-banner'

	private canvas!: HTMLCanvasElement
	private ctx!: CanvasRenderingContext2D
	private text: string = 'NEW ARRIVALS'
	private fps: number = 30
	private ROWS: number = 9
	private TEXT_ROWS: number = 7
	private cols: number = 0
	private dotSize: number = 6
	private dotGap: number = 2
	private leftPointer: number = 0
	private furthestLeft: number = 0
	private offsetX: number = 0
	private offsetY: number = 0
	private message: boolean[][] = []
	private animTimeoutId: number = 0
	private animFrameId: number = 0
	private resizeTimeout: number = 0
	private ledColor: string = '#ff0000'
	private boundOnResize: () => void

	constructor() {
		super()
		this.boundOnResize = this.onResize.bind(this)
	}

	override connectedCallback() {
		super.connectedCallback()
		this.canvas = q.rs('canvas', this)
		const ctx = this.canvas.getContext('2d')
		if (!ctx) return
		this.ctx = ctx
		this.text = (this.dataset.text || 'NEW ARRIVALS').toUpperCase()
		this.fps = parseInt(this.dataset.speed || '30')
		this.ledColor = getComputedStyle(this).getPropertyValue('--led-color').trim() || '#ff0000'
		this.setup()
		window.addEventListener('resize', this.boundOnResize)
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		if (this.animTimeoutId) clearTimeout(this.animTimeoutId)
		if (this.animFrameId) cancelAnimationFrame(this.animFrameId)
		window.removeEventListener('resize', this.boundOnResize)
	}

	private onResize() {
		clearTimeout(this.resizeTimeout)
		this.resizeTimeout = window.setTimeout(() => this.setup(), 200)
	}

	private setup() {
		if (this.animTimeoutId) clearTimeout(this.animTimeoutId)
		if (this.animFrameId) cancelAnimationFrame(this.animFrameId)

		const isMobile = window.innerWidth <= 749
		this.dotSize = isMobile ? 4 : 6
		this.dotGap = isMobile ? 1.5 : 2

		const cellSize = this.dotSize + this.dotGap
		const containerWidth = this.offsetWidth
		this.cols = Math.floor(containerWidth / cellSize)

		const dpr = window.devicePixelRatio || 1
		const gridWidth = this.cols * cellSize
		const gridHeight = this.ROWS * cellSize
		this.offsetX = (containerWidth - gridWidth) / 2
		this.offsetY = 0
		this.canvas.width = containerWidth * dpr
		this.canvas.height = gridHeight * dpr
		this.canvas.style.width = `${containerWidth}px`
		this.canvas.style.height = `${gridHeight}px`
		this.ctx.scale(dpr, dpr)

		this.message = this.textToLED(this.text + '   ')
		this.leftPointer = this.cols + 1
		this.furthestLeft = -this.message.length

		this.draw()
	}

	private renderFrame() {
		const cellSize = this.dotSize + this.dotGap
		const canvasW = this.canvas.width / (window.devicePixelRatio || 1)
		const canvasH = this.canvas.height / (window.devicePixelRatio || 1)
		const radius = this.dotSize / 2

		this.ctx.clearRect(0, 0, canvasW, canvasH)

		for (let row = 0; row < this.ROWS; row++) {
			for (let col = 0; col < this.cols; col++) {
				const msgCol = col - this.leftPointer
				const textRow = row - 1
				const isOn = textRow >= 0 && textRow < this.TEXT_ROWS && msgCol >= 0 && msgCol < this.message.length && this.message[msgCol][textRow]

				const cx = this.offsetX + col * cellSize + radius
				const cy = this.offsetY + row * cellSize + radius

				this.ctx.beginPath()
				this.ctx.arc(cx, cy, radius, 0, Math.PI * 2)

				if (isOn) {
					this.ctx.fillStyle = this.ledColor
					this.ctx.shadowColor = this.ledColor
					this.ctx.shadowBlur = 5
				} else {
					this.ctx.fillStyle = '#121212'
					this.ctx.shadowColor = 'transparent'
					this.ctx.shadowBlur = 0
				}

				this.ctx.fill()
			}
		}

		this.ctx.shadowColor = 'transparent'
		this.ctx.shadowBlur = 0
	}

	private textToLED(theWord: string): boolean[][] {
		const theMessage: boolean[][][] = []
		for (let i = 0; i < theWord.length; i++) {
			theMessage.push(this.charToLED(theWord.charAt(i)))
			theMessage.push(this.charToLED(''))
		}
		const flat: boolean[][] = []
		return flat.concat(...theMessage)
	}

	private charToLED(c: string): boolean[][] {
		const T = true
		const F = false
		switch (c) {
			case 'A': return [[F,F,T,T,T,T,T],[F,T,F,F,T,F,F],[T,F,F,F,T,F,F],[F,T,F,F,T,F,F],[F,F,T,T,T,T,T]]
			case 'B': return [[T,T,T,T,T,T,T],[T,F,F,T,F,F,T],[T,F,F,T,F,F,T],[T,F,F,T,F,F,T],[F,T,T,F,T,T,F]]
			case 'C': return [[F,T,T,T,T,T,F],[T,F,F,F,F,F,T],[T,F,F,F,F,F,T],[T,F,F,F,F,F,T],[F,T,F,F,F,T,F]]
			case 'D': return [[T,T,T,T,T,T,T],[T,F,F,F,F,F,T],[T,F,F,F,F,F,T],[T,F,F,F,F,F,T],[F,T,T,T,T,T,F]]
			case 'E': return [[T,T,T,T,T,T,T],[T,F,F,T,F,F,T],[T,F,F,T,F,F,T],[T,F,F,T,F,F,T],[T,F,F,F,F,F,T]]
			case 'F': return [[T,T,T,T,T,T,T],[T,F,F,T,F,F,F],[T,F,F,T,F,F,F],[T,F,F,T,F,F,F],[T,F,F,F,F,F,F]]
			case 'G': return [[F,T,T,T,T,T,F],[T,F,F,F,F,F,T],[T,F,F,F,F,F,T],[T,F,F,F,T,F,T],[T,T,F,F,T,T,T]]
			case 'H': return [[T,T,T,T,T,T,T],[F,F,F,T,F,F,F],[F,F,F,T,F,F,F],[F,F,F,T,F,F,F],[T,T,T,T,T,T,T]]
			case 'I': return [[F,F,F,F,F,F,F],[T,F,F,F,F,F,T],[T,T,T,T,T,T,T],[T,F,F,F,F,F,T],[F,F,F,F,F,F,F]]
			case 'J': return [[F,F,F,F,F,T,F],[F,F,F,F,F,F,T],[T,F,F,F,F,F,T],[T,T,T,T,T,T,F],[T,F,F,F,F,F,F]]
			case 'K': return [[T,T,T,T,T,T,T],[F,F,F,T,F,F,F],[F,F,T,F,T,F,F],[F,T,F,F,F,T,F],[T,F,F,F,F,F,T]]
			case 'L': return [[T,T,T,T,T,T,T],[F,F,F,F,F,F,T],[F,F,F,F,F,F,T],[F,F,F,F,F,F,T],[F,F,F,F,F,F,T]]
			case 'M': return [[T,T,T,T,T,T,T],[F,T,F,F,F,F,F],[F,F,T,F,F,F,F],[F,T,F,F,F,F,F],[T,T,T,T,T,T,T]]
			case 'N': return [[T,T,T,T,T,T,T],[F,F,T,F,F,F,F],[F,F,F,T,F,F,F],[F,F,F,F,T,F,F],[T,T,T,T,T,T,T]]
			case 'O': return [[F,T,T,T,T,T,F],[T,F,F,F,F,F,T],[T,F,F,F,F,F,T],[T,F,F,F,F,F,T],[F,T,T,T,T,T,F]]
			case 'P': return [[T,T,T,T,T,T,T],[T,F,F,T,F,F,F],[T,F,F,T,F,F,F],[T,F,F,T,F,F,F],[F,T,T,F,F,F,F]]
			case 'Q': return [[F,T,T,T,T,T,F],[T,F,F,F,F,F,T],[T,F,F,F,T,F,T],[T,F,F,F,F,T,F],[F,T,T,T,T,F,T]]
			case 'R': return [[T,T,T,T,T,T,T],[T,F,F,T,F,F,F],[T,F,F,T,F,F,F],[T,F,F,T,F,F,F],[F,T,T,F,T,T,T]]
			case 'S': return [[F,T,T,F,F,F,T],[T,F,F,T,F,F,T],[T,F,F,T,F,F,T],[T,F,F,T,F,F,T],[T,F,F,F,T,T,F]]
			case 'T': return [[T,F,F,F,F,F,F],[T,F,F,F,F,F,F],[T,T,T,T,T,T,T],[T,F,F,F,F,F,F],[T,F,F,F,F,F,F]]
			case 'U': return [[T,T,T,T,T,T,F],[F,F,F,F,F,F,T],[F,F,F,F,F,F,T],[F,F,F,F,F,F,T],[T,T,T,T,T,T,F]]
			case 'V': return [[T,T,T,T,T,F,F],[F,F,F,F,F,T,F],[F,F,F,F,F,F,T],[F,F,F,F,F,T,F],[T,T,T,T,T,F,F]]
			case 'W': return [[T,T,T,T,T,T,F],[F,F,F,F,F,F,T],[F,F,F,F,T,T,F],[F,F,F,F,F,F,T],[T,T,T,T,T,T,F]]
			case 'X': return [[T,F,F,F,F,F,T],[F,T,T,F,T,T,F],[F,F,F,T,F,F,F],[F,T,T,F,T,T,F],[T,F,F,F,F,F,T]]
			case 'Y': return [[T,F,F,F,F,F,F],[F,T,F,F,F,F,F],[F,F,T,T,T,T,T],[F,T,F,F,F,F,F],[T,F,F,F,F,F,F]]
			case 'Z': return [[T,F,F,F,F,T,T],[T,F,F,F,T,F,T],[T,F,F,T,F,F,T],[T,F,T,F,F,F,T],[T,T,F,F,F,F,T]]
			default: return [[F,F,F,F,F,F,F]]
		}
	}

	private draw() {
		this.animTimeoutId = window.setTimeout(() => {
			this.animFrameId = requestAnimationFrame(() => {
				if (this.leftPointer === this.furthestLeft) {
					this.leftPointer = this.cols + 1
				}

				this.renderFrame()
				this.leftPointer--
				this.draw()
			})
		}, 1000 / this.fps)
	}
}
